import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { describePath } from '../lib/paths';
import type { ColumnMapping, ImportResult } from '../lib/types';
import { Dropzone, Spinner, useToast } from '../components/ui';
import { useProjectContext } from './ProjectLayout';

const MAPPING_FIELDS: Array<[keyof ColumnMapping, string, string]> = [
  ['destination', 'Destination / city', 'MILAN, LIVIGNO…'],
  ['day', 'Date', '28-jun, 29-jun…'],
  ['km', 'Kilometres', '210, 260…'],
  ['pilot', 'Pilot price', '5.334,59 €'],
  ['copilot', 'Copilot price', '2.644,29 €'],
  ['single', 'Single price', '6.830,30 €'],
];

type SheetSource = { kind: 'file'; file: File } | { kind: 'url'; url: string };

export function ImportPage() {
  const { project, setProject } = useProjectContext();
  const toast = useToast();
  const navigate = useNavigate();

  const [busy, setBusy] = useState<'docx' | 'sheet' | 'apply' | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [choices, setChoices] = useState<Record<string, 'current' | 'incoming'>>({});
  const [sheetSource, setSheetSource] = useState<SheetSource | null>(null);
  const [sheetUrl, setSheetUrl] = useState('');
  const [mapping, setMapping] = useState<ColumnMapping | null>(null);
  const [headerRow, setHeaderRow] = useState<number | null>(null);
  const [showMapper, setShowMapper] = useState(false);

  const accept = (res: ImportResult) => {
    setResult(res);
    setChoices(Object.fromEntries(res.conflicts.map((c) => [c.path, c.recommended])));
    if (res.source === 'sheet') {
      setMapping(res.mapping ?? null);
      setHeaderRow(res.headerRow ?? null);
      setShowMapper(!res.autoMapped);
    }
  };

  const runDocx = async (file: File) => {
    setBusy('docx');
    setResult(null);
    try {
      accept(await api.importDocx(project.id, file));
    } catch (e) {
      toast.push((e as Error).message, true);
    } finally {
      setBusy(null);
    }
  };

  const runSheet = async (source: SheetSource, opts: { sheet?: string; mapping?: ColumnMapping; headerRow?: number } = {}) => {
    setBusy('sheet');
    setSheetSource(source);
    try {
      const res = source.kind === 'file'
        ? await api.importSheet(project.id, source.file, opts)
        : await api.importSheetUrl(project.id, source.url, opts);
      accept(res);
    } catch (e) {
      toast.push((e as Error).message, true);
    } finally {
      setBusy(null);
    }
  };

  const rereadSheet = (overrides: { sheet?: string } = {}) => {
    if (!sheetSource || !result) return;
    void runSheet(sheetSource, {
      sheet: overrides.sheet ?? result.sheetName,
      mapping: overrides.sheet ? undefined : mapping ?? undefined,
      headerRow: overrides.sheet ? undefined : headerRow ?? undefined,
    });
  };

  const apply = async () => {
    if (!result) return;
    setBusy('apply');
    try {
      const updated = await api.applyImport(project.id, result.token, choices);
      setProject({ ...updated, issues: (await api.preflight(project.id)).issues });
      toast.push('Imported — check the text in step 2');
      setResult(null);
      navigate(`/projects/${project.id}/content`);
    } catch (e) {
      toast.push((e as Error).message, true);
    } finally {
      setBusy(null);
    }
  };

  const lowConfidence = Object.entries(result?.confidence ?? {}).filter(([, level]) => level === 'low').map(([path]) => path);
  const columnOptions = result?.columns ?? [];

  return (
    <>
      <div className="grid-2">
        <div className="panel">
          <h2 className="section">Booklet text (.docx)</h2>
          <Dropzone
            title="Drop the Word file here"
            hint="Title, route, schedule, day stories, experience and packages"
            accept=".docx"
            busy={busy === 'docx'}
            onFile={(file) => void runDocx(file)}
          />
          <a className="form-link" href={api.importFormUrl(project.id, 'docx')} download>
            <DownloadIcon /> Download the empty Word form
          </a>
          <div className="hint-line">Send it to whoever writes the tour text, then upload it here.</div>
        </div>

        <div className="panel">
          <h2 className="section">Prices &amp; itinerary (.xlsx / .csv)</h2>
          <Dropzone
            title="Drop the spreadsheet here"
            hint="Dates, KM, Pilot / Copilot / Single prices, bikes, included & not included"
            accept=".xlsx,.xls,.csv"
            busy={busy === 'sheet' && sheetSource?.kind === 'file'}
            onFile={(file) => void runSheet({ kind: 'file', file })}
          />
          <div className="or">or paste a Google Sheets link</div>
          <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
            <input
              className="search"
              style={{ flex: 1 }}
              placeholder="https://docs.google.com/spreadsheets/d/…"
              value={sheetUrl}
              onChange={(e) => setSheetUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sheetUrl && void runSheet({ kind: 'url', url: sheetUrl })}
            />
            <button className="btn" disabled={!sheetUrl || busy === 'sheet'} onClick={() => void runSheet({ kind: 'url', url: sheetUrl })}>
              {busy === 'sheet' && sheetSource?.kind === 'url' ? <Spinner /> : 'Import'}
            </button>
          </div>
          <div className="hint-line">The sheet must be shared as “Anyone with the link can view”.</div>
          <a className="form-link" href={api.importFormUrl(project.id, 'xlsx')} download>
            <DownloadIcon /> Download the empty Excel form
          </a>
          <div className="hint-line">Already has the right columns, so prices and days are read automatically.</div>
        </div>
      </div>

      {result && (
        <div className="panel">
          <div className="row" style={{ marginBottom: 12 }}>
            <h2 className="section" style={{ margin: 0 }}>Check before adding — {result.filename}</h2>
            <div style={{ flex: 1 }} />
            <button className="btn ghost" onClick={() => setResult(null)}>Discard</button>
            <button className="btn primary" disabled={busy === 'apply'} onClick={() => void apply()}>
              {busy === 'apply' ? <><Spinner /> Adding…</> : 'Add to booklet'}
            </button>
          </div>

          {result.stats && (
            <div className="row sub" style={{ marginBottom: 12, gap: 18 }}>
              <span>{result.stats.paragraphs} paragraphs read</span>
              <span>{result.stats.mapped} recognised</span>
              <span>{result.stats.unmapped} not recognised</span>
              <span>{result.stats.photoMarkers} photo markers</span>
            </div>
          )}

          {lowConfidence.length > 0 && (
            <div className="issues" style={{ marginBottom: 14 }}>
              <div className="sub">Please double-check these — the file was unclear:</div>
              {lowConfidence.map((path) => (
                <div className="issue" key={path}>{describePath(path.replace(/\[(\d+)\]/g, '.$1'))}{readValue(result, path)}</div>
              ))}
            </div>
          )}

          {result.source === 'sheet' && (
            <div className="sheet-bar">
              {(result.sheets?.length ?? 0) > 1 && (
                <label className="row" style={{ gap: 8 }}>
                  <span className="sub">Sheet</span>
                  <select className="inline-select" value={result.sheetName} onChange={(e) => rereadSheet({ sheet: e.target.value })}>
                    {result.sheets!.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </label>
              )}
              <span className={result.autoMapped ? 'ok-text' : 'warn-text'}>
                {result.autoMapped ? '✓ Columns recognised automatically' : '⚠ Columns not recognised — match them below'}
              </span>
              {!!result.pending?.length && <span className="warn-text">Still pending: {result.pending.join(', ')}</span>}
              <div style={{ flex: 1 }} />
              <button className="btn small" onClick={() => setShowMapper((v) => !v)}>{showMapper ? 'Hide column matching' : 'Match columns'}</button>
            </div>
          )}

          {result.source === 'sheet' && showMapper && mapping && (
            <div className="mapper">
              <div className="sub" style={{ marginBottom: 10 }}>Tell us which column holds what, then read the sheet again.</div>
              <div className="grid-3">
                <div className="field">
                  <label>Headers are on row</label>
                  <select value={headerRow ?? -1} onChange={(e) => setHeaderRow(Number(e.target.value))}>
                    <option value={-1}>No header row</option>
                    {(result.preview ?? []).map((row, i) => (
                      <option key={i} value={i}>Row {i + 1}: {row.filter(Boolean).slice(0, 3).join(' · ') || '(empty)'}</option>
                    ))}
                  </select>
                </div>
                {MAPPING_FIELDS.map(([key, label, example]) => (
                  <div className="field" key={key}>
                    <label>{label}</label>
                    <select
                      value={mapping[key] ?? ''}
                      onChange={(e) => setMapping({ ...mapping, [key]: e.target.value === '' ? null : Number(e.target.value) })}
                    >
                      <option value="">— not in this sheet —</option>
                      {columnOptions.map((c) => (
                        <option key={c.index} value={c.index}>Column {c.letter}{c.header ? ` · ${c.header}` : ''}</option>
                      ))}
                    </select>
                    <div className="hint">e.g. {example}</div>
                  </div>
                ))}
              </div>
              <div className="row end">
                <button className="btn primary" disabled={busy === 'sheet'} onClick={() => rereadSheet()}>
                  {busy === 'sheet' ? <><Spinner /> Reading…</> : 'Read the sheet again'}
                </button>
              </div>
            </div>
          )}

          <Summary result={result} />

          {result.conflicts.length > 0 && (
            <>
              <h2 className="section" style={{ marginTop: 18 }}>
                {result.conflicts.length} difference{result.conflicts.length === 1 ? '' : 's'} with what is already in the booklet
              </h2>
              {result.conflicts.map((conflict) => (
                <div className="conflict" key={conflict.path}>
                  <div className="label">{conflict.label}</div>
                  <div className="options">
                    {(['current', 'incoming'] as const).map((side) => (
                      <div
                        key={side}
                        className={`opt ${choices[conflict.path] === side ? 'chosen' : ''}`}
                        onClick={() => setChoices((c) => ({ ...c, [conflict.path]: side }))}
                      >
                        <div className="src">
                          {side === 'current' ? 'Keep what is there' : 'Use the file'}
                          {conflict.recommended === side ? ' · recommended' : ''}
                        </div>
                        <div>{side === 'current' ? conflict.current : conflict.incoming}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}

          {result.preview && (
            <details style={{ marginTop: 16 }}>
              <summary className="sub" style={{ cursor: 'pointer' }}>Show the first rows of the sheet</summary>
              <div style={{ overflowX: 'auto', marginTop: 8 }}>
                <table className="table plain grid">
                  <thead>
                    <tr><th />{columnOptions.map((c) => <th key={c.index}>{c.letter}</th>)}</tr>
                  </thead>
                  <tbody>
                    {result.preview.map((row, i) => (
                      <tr key={i} className={i === result.headerRow ? 'header-row' : ''}>
                        <td className="sub">{i + 1}</td>
                        {columnOptions.map((c) => <td key={c.index}>{row[c.index] ?? ''}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}

      {!!project.content.unmapped.length && (
        <div className="panel">
          <h2 className="section">Text we could not place ({project.content.unmapped.length})</h2>
          <div className="sub" style={{ marginBottom: 8 }}>
            Nothing from an import is thrown away. These lines had no obvious home — copy them into the right place in step 2.
          </div>
          {project.content.unmapped.slice(0, 30).map((item, i) => (
            <div key={i} className="issue">{item.text}</div>
          ))}
        </div>
      )}
    </>
  );
}

function readValue(result: ImportResult, path: string): string {
  const m = path.match(/^(\w+)\[(\d+)\]\.(\w+)$/);
  const patch = result.patch as Record<string, unknown>;
  if (m) {
    const list = patch[m[1]] as Array<Record<string, unknown>> | undefined;
    const item = list?.find((x) => x.day === Number(m[2])) ?? list?.[Number(m[2])];
    return item?.[m[3]] != null ? ` — “${String(item[m[3]])}”` : '';
  }
  return '';
}

function Summary({ result }: { result: ImportResult }) {
  const p = result.patch;
  const rows: Array<[string, string]> = [];
  if (p.tour?.title) rows.push(['Title', p.tour.title]);
  if (p.tour?.durationDays) rows.push(['Duration', `${p.tour.durationDays} days`]);
  if (p.route?.cities?.length) rows.push(['Route', p.route.cities.join(' → ')]);
  if (p.route?.totalDistanceKm) rows.push(['Distance', `${p.route.totalDistanceKm} KM`]);
  if (p.schedule?.length) rows.push(['Schedule', `${p.schedule.length} entries`]);
  if (p.days?.length) rows.push(['Day pages', `${p.days.length}`]);
  if (p.experience?.length) rows.push(['Experience', p.experience.map((e) => e.label).join(', ')]);
  if (p.packages?.length) {
    rows.push(['Packages', p.packages.map((x) => `${x.name} ${x.price.amount ?? 'pending'}`).join(' · ')]);
  }
  if (p.bikes?.length) rows.push(['Bikes', p.bikes.map((b) => `${b.brand} ${b.model}`).join(', ')]);
  if (p.itinerary?.length) rows.push(['Itinerary rows', `${p.itinerary.length}`]);
  if (p.included?.length) rows.push(['Included', `${p.included.length} items`]);
  if (p.notIncluded?.length) rows.push(['Not included', `${p.notIncluded.length} items`]);

  if (!rows.length) return <div className="sub">Nothing recognisable in that file yet.</div>;

  return (
    <table className="table plain">
      <tbody>
        {rows.map(([key, value]) => (
          <tr key={key}>
            <td style={{ width: 160 }} className="sub">{key}</td>
            <td>{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
